"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { PhoneCall, CircleQuestionMark as HelpCircle, Clock, ShieldAlert, ArrowRight, CircleCheck as CheckCircle2, Wrench } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
interface FaqItem {
  id: string;
  question: string;
  answer: string;
  category: string;
}
const faqData: FaqItem[] = [{
  id: "item-1",
  category: "Efficiency & Value",
  question: "Is a tankless water heater worth the investment for Jonesboro homes?",
  answer: "Yes. Tankless water heaters only heat water on demand rather than keeping 50 gallons hot all day. This reduces energy usage by up to 30%, frees up significant closet space, and doubles the service life of traditional tank systems from 10 years to over 20 years."
}, {
  id: "item-2",
  category: "Emergency Replacement",
  question: "How fast can you replace a failed or leaking water heater in an emergency?",
  answer: "Our licensed emergency plumbers in Jonesboro are on call 24 hours a day, 7 days a week. We offer same-day emergency dispatch and rapid changeouts to restore hot water to your home with zero delay."
}, {
  id: "item-3",
  category: "Performance",
  question: "Can a tankless unit handle running multiple showers at the same time?",
  answer: "Absolutely, provided it is properly sized for your household's peak gallons-per-minute (GPM) demand. During our consultation, we calculate your simultaneous fixture usage to specify the exact flow rate needed so you never experience temperature drops."
}, {
  id: "item-4",
  category: "Maintenance",
  question: "What maintenance does a tankless water heater require?",
  answer: "Tankless units should be flushed once a year to clear mineral and limescale buildup from the internal heat exchanger. Routine descaling keeps your unit operating at peak thermal efficiency and protects your manufacturer warranty."
}, {
  id: "item-5",
  category: "Urgent Actions",
  question: "What should I do right now if my current water heater is leaking or making loud noises?",
  answer: "Immediately turn off the cold water shutoff valve above the heater or your main water supply. If it is electric, flip the circuit breaker off; if gas, rotate the gas control knob to 'Off'. Then call our 24/7 Jonesboro dispatch team for immediate assistance."
}];
const perksList = ["24/7 Immediate local emergency response", "Licensed, insured, and background-checked technicians", "Upfront pricing with no surprise overtime fees", "Top tankless brand certifications & full warranties"];
export default function TanklessFaq() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="tanklesswaterheaterfaqcta" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-t border-border overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        {/* Section Header */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="text-center max-w-3xl mx-auto mb-12 md:mb-16">
          <div className="inline-flex items-center gap-2 mb-3">
            <Badge variant="outline" className="px-3 py-1 font-medium text-xs tracking-wide uppercase">
              <HelpCircle className="size-3.5 text-accent-foreground" />
              Frequently Asked Questions
            </Badge>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold text-foreground tracking-tight leading-tight">
            Tankless Water Heater Questions & Answers
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground font-sans leading-relaxed">
            Get clear, honest information about upgrading to on-demand hot water, routine maintenance, and emergency plumbing replacements in Jonesboro, GA.
          </p>
        </motion.div>

        {/* 2-Column Grid: FAQs on left/top, Highlight / Emergency info on right */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-start mb-16">
          {/* FAQ Accordion Column */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.1
        }} variants={containerVariants} className="lg:col-span-7">
            <Accordion type="single" collapsible defaultValue="item-1" className="w-full space-y-4">
              {faqData.map((faq, i) => <div key={faq.id} data-index={i}>
                  <AccordionItem value={faq.id} className="border border-border rounded-xl bg-card px-5 sm:px-6 shadow-xs transition-[border-color,box-shadow] duration-200 hover:border-primary/40 data-[state=open]:border-primary/60 data-[state=open]:shadow-sm">
                    <AccordionTrigger className="text-left font-default text-base sm:text-lg font-semibold text-card-foreground py-5 hover:no-underline gap-4">
                      <span className="flex items-center gap-3">
                        <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-secondary text-xs font-bold text-secondary-foreground">
                          0{i + 1}
                        </span>
                        <span>{faq.question}</span>
                      </span>
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground font-sans text-sm sm:text-base leading-relaxed pt-1 pb-5 border-t border-border/60">
                      <p className="mb-2">{faq.answer}</p>
                      <span className="inline-block text-xs font-semibold text-accent-foreground/80 bg-muted px-2.5 py-0.5 rounded-md mt-2">
                        {faq.category}
                      </span>
                    </AccordionContent>
                  </AccordionItem>
                </div>)}
            </Accordion>
          </motion.div>

          {/* Right Highlight Box / Quick Assistance */}
          <motion.div initial="hidden" whileInView="visible" viewport={{
          once: true,
          amount: 0.1
        }} variants={containerVariants} className="lg:col-span-5 flex flex-col gap-6">
            <Card className="border-border bg-card shadow-sm rounded-xl">
              <CardContent className="p-6 sm:p-8 flex flex-col gap-6">
                <div className="flex items-center gap-3">
                  <div className="size-11 rounded-lg bg-primary/10 flex items-center justify-center text-primary">
                    <Wrench className="size-6" />
                  </div>
                  <div>
                    <h3 className="font-default font-bold text-lg sm:text-xl text-card-foreground">
                      Need a Direct Answer?
                    </h3>
                    <p className="text-xs sm:text-sm text-muted-foreground">
                      Our certified master plumbers are ready to help.
                    </p>
                  </div>
                </div>

                <div className="space-y-3 pt-2 border-t border-border">
                  {perksList.map((perk, i) => <div key={i} data-index={i} className="flex items-start gap-3">
                      <CheckCircle2 className="size-5 text-accent shrink-0 mt-0.5" />
                      <span className="text-sm font-medium text-card-foreground">{perk}</span>
                    </div>)}
                </div>

                <div className="bg-secondary rounded-lg p-4 flex items-center gap-3 text-secondary-foreground">
                  <Clock className="size-5 shrink-0 text-primary" />
                  <p className="text-xs sm:text-sm leading-snug">
                    Average response time in Jonesboro: <strong className="font-semibold">under 45 minutes</strong> for urgent water heater emergencies.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 pt-2">
                  <Button asChild variant="default" className="w-full justify-center">
                    <Link to="/contact/">
                      <span>Schedule Consultation</span>
                      <ArrowRight className="size-4 ml-1" />
                    </Link>
                  </Button>
                  <Button asChild variant="outline" className="w-full justify-center">
                    <Link to="/water-heater/">
                      <span>Explore Systems</span>
                    </Link>
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Emergency alert card */}
            <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-5 flex items-start gap-4">
              <ShieldAlert className="size-6 text-destructive shrink-0 mt-0.5" />
              <div>
                <h4 className="font-default font-semibold text-sm sm:text-base text-foreground">
                  Active Water Leak or Burst Tank?
                </h4>
                <p className="text-xs sm:text-sm text-muted-foreground mt-1">
                  Do not wait for property damage to escalate. Call our rapid response line right now.
                </p>
                <div className="mt-3">
                  <a href="tel:4708479076" className="inline-flex items-center gap-2 text-sm font-bold text-destructive hover:underline">
                    <PhoneCall className="size-4" />
                    <span>(470) 847-9076 — 24/7 Dispatch</span>
                  </a>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Final Integrated Call-To-Action Banner */}
        <motion.div initial="hidden" whileInView="visible" viewport={{
        once: true,
        amount: 0.2
      }} variants={containerVariants} className="rounded-2xl bg-primary text-primary-foreground p-8 sm:p-10 lg:p-12 shadow-lg relative overflow-hidden">
          {/* Subtle Decorative Elements */}
          <div className="absolute -right-16 -top-16 size-64 rounded-full bg-accent/15 blur-3xl pointer-events-none" />
          <div className="absolute -left-16 -bottom-16 size-64 rounded-full bg-primary-foreground/10 blur-3xl pointer-events-none" />

          <div className="relative z-10 flex flex-col lg:flex-row items-center justify-between gap-8 text-center lg:text-left">
            <div className="max-w-2xl">
              <Badge variant="secondary" className="mb-3 px-3 py-1 font-semibold text-xs tracking-wide uppercase">
                Ready for Endless Hot Water?
              </Badge>
              <h3 className="text-2xl sm:text-3xl lg:text-4xl font-default font-bold text-primary-foreground tracking-tight">
                Upgrade to a High-Efficiency Tankless System Today
              </h3>
              <p className="mt-3 text-sm sm:text-base text-primary-foreground/85 leading-relaxed">
                Contact Jonesboro Emergency Plumber for instant quotes, fast professional installation, and round-the-clock water heater support.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row items-center gap-4 w-full sm:w-auto shrink-0">
              <Button asChild size="lg" variant="secondary" className="w-full sm:w-auto font-semibold shadow-sm text-secondary-foreground">
                <Link to="/contact/">
                  <span>Get a Free Quote</span>
                  <ArrowRight className="size-4 ml-1.5" />
                </Link>
              </Button>

              <Button asChild size="lg" variant="outline" className="w-full sm:w-auto bg-transparent border-primary-foreground/30 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground">
                <a href="tel:4708479076" className="inline-flex items-center justify-center gap-2">
                  <PhoneCall className="size-4" />
                  <span>Call (470) 847-9076</span>
                </a>
              </Button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}